import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader } from '../../components/ui/Card';
import { ChevronDown, ChevronRight, Edit3, Save, X, Plus, Trash2, ArrowLeft } from 'lucide-react';

const metricLabels = {
  gain_attraction: 'Gain Attraction',
  loss_aversion: 'Loss Aversion',
  volatility_anxiety: 'Volatility Anxiety',
  intervention_risk: 'Intervention Risk'
};

const stateLabels = { low: 'Low (<50%)', high: 'High (≥50%)' };

const permutationLabels = [
  'GA− | LA− | VA− | IR−',
  'GA− | LA− | VA− | IR+',
  'GA− | LA− | VA+ | IR−',
  'GA− | LA− | VA+ | IR+',
  'GA− | LA+ | VA− | IR−',
  'GA− | LA+ | VA− | IR+',
  'GA− | LA+ | VA+ | IR−',
  'GA− | LA+ | VA+ | IR+',
  'GA+ | LA− | VA− | IR−',
  'GA+ | LA− | VA− | IR+',
  'GA+ | LA− | VA+ | IR−',
  'GA+ | LA− | VA+ | IR+',
  'GA+ | LA+ | VA− | IR−',
  'GA+ | LA+ | VA− | IR+',
  'GA+ | LA+ | VA+ | IR−',
  'GA+ | LA+ | VA+ | IR+',
];

const ManageArchetypes = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('archetypes');
  const [themes, setThemes] = useState([]);
  const [selectedThemeId, setSelectedThemeId] = useState(null);
  const [definitions, setDefinitions] = useState([]);
  const [blurbs, setBlurbs] = useState([]);
  const [expandedDef, setExpandedDef] = useState(null);
  const [editingDef, setEditingDef] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editingBlurb, setEditingBlurb] = useState(null);
  const [blurbForm, setBlurbForm] = useState({});
  const [newThemeMode, setNewThemeMode] = useState(false);
  const [newThemeName, setNewThemeName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [themesRes, blurbsRes] = await Promise.all([
        api.get('/archetypes/themes'),
        api.get('/archetypes/blurbs')
      ]);
      const themesData = themesRes.data;
      setThemes(themesData);
      setBlurbs(blurbsRes.data);
      if (themesData.length > 0 && !selectedThemeId) {
        setSelectedThemeId(themesData[0].id);
      }
    } catch (err) {
      console.error('Failed to load archetype data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedThemeId) loadDefinitions();
  }, [selectedThemeId]);

  const loadDefinitions = async () => {
    try {
      const res = await api.get(`/archetypes/themes/${selectedThemeId}/definitions`);
      setDefinitions(res.data);
    } catch (err) {
      console.error('Failed to load definitions:', err);
    }
  };

  const handleSaveDefinition = async (id) => {
    setSaving(true);
    try {
      await api.put(`/archetypes/definitions/${id}`, editForm);
      setEditingDef(null);
      loadDefinitions();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBlurb = async (id) => {
    setSaving(true);
    try {
      await api.put(`/archetypes/blurbs/${id}`, blurbForm);
      setEditingBlurb(null);
      const res = await api.get('/archetypes/blurbs');
      setBlurbs(res.data);
    } catch (err) {
      console.error('Failed to save blurb:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTheme = async () => {
    setNewThemeMode(true);
    setNewThemeName('');
  };

  const handleConfirmTheme = async () => {
    if (!newThemeName.trim()) return;
    try {
      await api.post('/archetypes/themes', { name: newThemeName.trim() });
      setNewThemeMode(false);
      setNewThemeName('');
      await loadData();
      // Select the newly created theme (last one)
      const themesRes = await api.get('/archetypes/themes');
      const themesData = themesRes.data;
      setThemes(themesData);
      const newTheme = themesData[themesData.length - 1];
      setSelectedThemeId(newTheme.id);
      // Auto-expand first definition for editing
      setExpandedDef(null); // will reload definitions
    } catch (err) {
      console.error('Failed to create theme:', err);
      alert('Failed to create theme');
    }
  };

  const handleDeleteTheme = async (id) => {
    if (!confirm('Delete this theme and all its archetype names?')) return;
    try {
      await api.delete(`/archetypes/themes/${id}`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete theme');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p style={{ color: theme.textSecondary }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <header className="border-b" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Investor Personality Archetypes</h1>
            </div>
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin Panel
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 rounded-lg" style={{ backgroundColor: theme.surface }}>
        {['archetypes', 'blurbs'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="px-4 py-2 rounded-md text-sm font-medium transition-colors flex-1"
            style={{
              backgroundColor: activeTab === tab ? theme.primary : 'transparent',
              color: activeTab === tab ? '#fff' : theme.textSecondary
            }}
          >
            {tab === 'archetypes' ? 'Archetypes & Themes' : 'Metric Blurbs'}
          </button>
        ))}
      </div>

      {/* ARCHETYPES TAB */}
      {activeTab === 'archetypes' && (
        <div>
          {/* Theme selector */}
          <Card className="mb-6">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium" style={{ color: theme.text }}>Theme:</label>
                  <select
                    value={selectedThemeId || ''}
                    onChange={e => setSelectedThemeId(parseInt(e.target.value))}
                    className="px-3 py-2 rounded-lg border text-sm"
                    style={{ borderColor: theme.border, backgroundColor: theme.surface, color: theme.text }}
                  >
                    {themes.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  {newThemeMode ? (
                    <div className="flex items-center gap-2">
                      <input
                        value={newThemeName}
                        onChange={e => setNewThemeName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleConfirmTheme()}
                        placeholder="Theme name..."
                        autoFocus
                        className="px-3 py-1 rounded-lg border text-sm"
                        style={{ borderColor: theme.border, backgroundColor: theme.surface, color: theme.text, width: '160px' }}
                      />
                      <Button onClick={handleConfirmTheme} style={{ padding: '4px 10px', fontSize: '0.8rem' }}>Create</Button>
                      <Button variant="ghost" onClick={() => setNewThemeMode(false)} style={{ padding: '4px 8px' }}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button variant="ghost" onClick={handleCreateTheme} style={{ padding: '6px 10px' }}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  )}
                  {themes.length > 1 && (
                    <Button variant="ghost" onClick={() => handleDeleteTheme(selectedThemeId)} style={{ padding: '6px 10px' }}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
                <p className="text-xs" style={{ color: theme.textSecondary }}>
                  Switching themes changes archetype names only. Taglines and narratives stay the same.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Archetype list */}
          <div className="space-y-3">
            {definitions.map((def, idx) => (
              <Card key={def.id}>
                <CardContent className="pt-3 pb-3">
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedDef(expandedDef === def.id ? null : def.id)}
                  >
                    <div className="flex items-center gap-3">
                      {expandedDef === def.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span className="text-xs font-mono px-2 py-1 rounded" style={{ backgroundColor: theme.surface, color: theme.textSecondary }}>
                        {permutationLabels[idx]}
                      </span>
                      <span className="font-semibold" style={{ color: def.name ? theme.text : theme.textSecondary }}>
                        {def.name || '(click to name this archetype)'}
                      </span>
                    </div>
                    <span className="text-sm italic" style={{ color: theme.textSecondary }}>"{def.tagline}"</span>
                  </div>

                  {expandedDef === def.id && (
                    <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${theme.border}` }}>
                      {editingDef === def.id ? (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium mb-1" style={{ color: theme.textSecondary }}>Name</label>
                            <input
                              value={editForm.name || ''}
                              onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                              className="w-full px-3 py-2 rounded-lg border text-sm"
                              style={{ borderColor: theme.border, backgroundColor: theme.surface, color: theme.text }}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1" style={{ color: theme.textSecondary }}>Tagline</label>
                            <input
                              value={editForm.tagline || ''}
                              onChange={e => setEditForm({ ...editForm, tagline: e.target.value })}
                              className="w-full px-3 py-2 rounded-lg border text-sm"
                              style={{ borderColor: theme.border, backgroundColor: theme.surface, color: theme.text }}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1" style={{ color: theme.textSecondary }}>Woven Narrative</label>
                            <textarea
                              value={editForm.woven_narrative || ''}
                              onChange={e => setEditForm({ ...editForm, woven_narrative: e.target.value })}
                              rows={6}
                              className="w-full px-3 py-2 rounded-lg border text-sm"
                              style={{ borderColor: theme.border, backgroundColor: theme.surface, color: theme.text }}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => handleSaveDefinition(def.id)} disabled={saving}>
                              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                            </Button>
                            <Button variant="ghost" onClick={() => setEditingDef(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-medium mb-1" style={{ color: theme.textSecondary }}>Woven Narrative:</p>
                            <p className="text-sm leading-relaxed" style={{ color: theme.text }}>{def.woven_narrative}</p>
                          </div>
                          <Button variant="ghost" onClick={() => { setEditingDef(def.id); setEditForm({ name: def.name, tagline: def.tagline, woven_narrative: def.woven_narrative }); }}>
                            <Edit3 className="w-4 h-4 mr-1" /> Edit
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* BLURBS TAB */}
      {activeTab === 'blurbs' && (
        <div className="space-y-4">
          <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>
            These modular paragraphs combine to form the standalone section of each archetype report. Edit carefully — changes affect all archetypes that use each blurb.
          </p>
          {['gain_attraction', 'loss_aversion', 'volatility_anxiety', 'intervention_risk'].map(metric => (
            <Card key={metric}>
              <CardHeader>
                <h3 className="font-semibold" style={{ color: theme.text }}>{metricLabels[metric]}</h3>
              </CardHeader>
              <CardContent className="pt-2 pb-4">
                <div className="space-y-4">
                  {['low', 'high'].map(state => {
                    const blurb = blurbs.find(b => b.metric_key === metric && b.state === state);
                    if (!blurb) return null;
                    const isEditing = editingBlurb === blurb.id;

                    return (
                      <div key={state} className="p-4 rounded-lg" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium" style={{ color: theme.text }}>
                            {stateLabels[state]} — "{blurb.title}"
                          </span>
                          {!isEditing && (
                            <Button variant="ghost" onClick={() => { setEditingBlurb(blurb.id); setBlurbForm({ title: blurb.title, body: blurb.body }); }} style={{ padding: '4px 8px' }}>
                              <Edit3 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        {isEditing ? (
                          <div className="space-y-2">
                            <input
                              value={blurbForm.title || ''}
                              onChange={e => setBlurbForm({ ...blurbForm, title: e.target.value })}
                              placeholder="Title"
                              className="w-full px-3 py-2 rounded-lg border text-sm"
                              style={{ borderColor: theme.border, backgroundColor: '#fff', color: theme.text }}
                            />
                            <textarea
                              value={blurbForm.body || ''}
                              onChange={e => setBlurbForm({ ...blurbForm, body: e.target.value })}
                              rows={5}
                              className="w-full px-3 py-2 rounded-lg border text-sm"
                              style={{ borderColor: theme.border, backgroundColor: '#fff', color: theme.text }}
                            />
                            <div className="flex gap-2">
                              <Button onClick={() => handleSaveBlurb(blurb.id)} disabled={saving} style={{ padding: '4px 12px', fontSize: '0.8rem' }}>
                                <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving...' : 'Save'}
                              </Button>
                              <Button variant="ghost" onClick={() => setEditingBlurb(null)} style={{ padding: '4px 12px', fontSize: '0.8rem' }}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm leading-relaxed" style={{ color: theme.textSecondary }}>{blurb.body}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </main>
    </div>
  );
};

export default ManageArchetypes;
